import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Collapse, Icon } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { TranslatableText } from 'src/feature/localization/component/atom/TranslatableText'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { If } from 'src/component/common/original/atom/If'
import { AppUnstyledButton } from 'src/component/common/analog/molecule/button/AppUnstyledButton'
import { iconMapping } from 'src/util/icon-mapping'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { NavModel } from 'src/model/Nav'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { mergeTranslationWithObjects } from 'src/feature/localization/util/localization.helper'
import { Sidebar } from 'src/feature/sidebar/component/Sidebar'
import { useTourSteps } from 'src/hook/useTourSteps'
import { expandedSidebarDropdownSx } from 'src/feature/sidebar/component/sidebarDropdownStyles'

export type ELearningSidebarProps = {
  isOpen: boolean
}

export const ELearningSidebar = observer(({ isOpen }: ELearningSidebarProps) => {
  const { translation } = useLocalizationStore()
  const { getStepClass } = useTourSteps()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { eLearningSidebar } = translation.components

  const navLinks: NavModel<'props.link'> = {
    learningOverview: {
      props: {
        link: appRoutePath.learning,
        hidden: false,
      },
    },
    courses: {
      props: {
        link: appRoutePath.myCourses,
        hidden: false,
      },
    },
  }

  const desktopNav = mergeTranslationWithObjects(eLearningSidebar.nav, navLinks) as unknown as NavModel

  return (
    <>
      <AppBox
        alignItems='center'
        width='100%'
        sx={{
          cursor: 'pointer',
          justifyContent: {
            xs: 'center',
            md: 'space-between',
          } }}
        onClick={() => isOpen && setIsMenuOpen(!isMenuOpen)}
      >
        <AppBox justifyContent='center' alignItems='center' gap={7} className={getStepClass('eLearningV2')}>
          <Icon
            sx={{
              color: 'text.primary',
              transition: 'color 0.3s ease',
              fontSize: 16,
              height: 16,
              width: 16,
            }}
            component={iconMapping['SchoolRoundedIcon']}
          />
          {isOpen && <TranslatableText
            component={'p'}
            textValue={eLearningSidebar.title()}
            sx={{
              transition: 'color 0.3s ease',
              color: 'text.primary',
              fontSize: '14px',
            }}
          />}
        </AppBox>
        <AppUnstyledButton
          size='small'
          sx={{
            width: 20,
            height: 20,
            minWidth: 20,
            minHeight: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            backgroundColor: isMenuOpen && isOpen ? 'rgba(125, 235, 228, 0.2)' : 'transparent',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(125, 235, 228, 0.12)',
            },
          }}
        >
          <If condition={isMenuOpen && isOpen}>
            <ExpandMoreRoundedIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
          <If condition={!isMenuOpen && isOpen}>
            <ChevronRightRoundedIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
        </AppUnstyledButton>
      </AppBox>
      <Collapse
        in={isMenuOpen && isOpen}
        sx={{
          width: '100%',
          ...expandedSidebarDropdownSx,
          '& .link-label': {
            fontSize: '14px',
          },
        }}
        mountOnEnter
        unmountOnExit
      >
        <Sidebar nav={desktopNav} />
      </Collapse>
    </>
  )
})
